import { Link, useParams } from 'react-router-dom';
import usePageMeta from '@/hooks/usePageMeta';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import RiskBadge from '@/components/base/RiskBadge';
import IncidentCard from '@/components/base/IncidentCard';
import { findIncidentById, incidents } from '@/mocks/incidents';

export default function IncidentDetail() {
  const { id } = useParams<{ id: string }>();
  const incident = id ? findIncidentById(id) : undefined;

  usePageMeta({
    title: incident
      ? `${incident.title}｜事件解读 - 黑料雷达`
      : '事件未找到 - 黑料雷达',
    description: incident
      ? incident.summary
      : '该事件记录不存在或已被合并，请返回事件库查看其他安全事件整理。',
    keywords: incident
      ? `${incident.categoryName},${incident.tags.join(',')},网站黑料,安全事件解读`
      : '网站黑料,安全事件',
    canonical: incident ? `/incident/${incident.id}` : '/incidents',
    ogType: 'article',
    jsonLd: incident
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: incident.title,
            description: incident.summary,
            datePublished: incident.date,
            dateModified: incident.date,
            inLanguage: 'zh-CN',
            articleSection: incident.categoryName,
            keywords: incident.tags.join(','),
            author: { '@type': 'Organization', name: '黑料雷达' },
            publisher: { '@type': 'Organization', name: '黑料雷达' },
            mainEntityOfPage: `https://worswgu.cn/incident/${incident.id}`,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://worswgu.cn/' },
              { '@type': 'ListItem', position: 2, name: '事件库', item: 'https://worswgu.cn/incidents' },
              {
                '@type': 'ListItem',
                position: 3,
                name: incident.title,
                item: `https://worswgu.cn/incident/${incident.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!incident) {
    return (
      <section className="mx-auto flex max-w-3xl flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-file-unknow-line text-2xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">事件记录不存在</h1>
        <p className="mt-3 text-sm leading-7 text-foreground-600">
          这条记录可能已被合并或更新。你可以回到事件库，用关键词重新检索。
        </p>
        <Link
          to="/incidents"
          className="mt-6 whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-700"
        >
          返回事件库
        </Link>
      </section>
    );
  }

  const related = incidents
    .filter((item) => item.id !== incident.id && item.category === incident.category)
    .slice(0, 2);
  const fallbackRelated = incidents.filter((item) => item.id !== incident.id).slice(0, 2);
  const relatedList = related.length > 0 ? related : fallbackRelated;

  return (
    <>
      <article>
        <header className="border-b border-background-200 bg-background-100/70">
          <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
            <Breadcrumbs
              items={[
                { label: '首页', to: '/' },
                { label: '事件库', to: '/incidents' },
                { label: incident.categoryName },
              ]}
            />
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <RiskBadge level={incident.riskLevel} />
              <Link
                to={`/topic/${incident.category}`}
                className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                {incident.categoryName}
              </Link>
              <span className="text-xs text-foreground-500">整理日期 {incident.date}</span>
              <span className="text-xs text-foreground-500">阅读约 {incident.readMinutes} 分钟</span>
            </div>
            <h1 className="mt-5 max-w-4xl font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              {incident.title}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-7 text-foreground-600">{incident.summary}</p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-building-2-line text-[13px]"></i>
                </span>
                涉及对象：{incident.platform}
              </span>
              {incident.tags.map((tag) => (
                <span key={tag} className="rounded-sm border border-background-300 px-2 py-0.5">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px] lg:gap-14">
            <div className="min-w-0">
              <div className="h-64 w-full overflow-hidden rounded-lg bg-background-200 md:h-96">
                <img
                  src={incident.cover}
                  alt={`${incident.title} 事件配图`}
                  title={`${incident.title} - 网站黑料事件解读`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <section className="mt-8 rounded-lg border border-accent-200 bg-accent-50 p-5 md:p-6">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-accent-900">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-information-line text-base"></i>
                  </span>
                  关键事实速览
                </h2>
                <ul className="mt-4 space-y-2.5">
                  {incident.facts.map((fact) => (
                    <li key={fact} className="flex items-start gap-2.5 text-sm leading-6 text-foreground-700">
                      <span className="mt-2 flex h-1.5 w-1.5 shrink-0 rounded-full bg-accent-600"></span>
                      {fact}
                    </li>
                  ))}
                </ul>
              </section>

              {incident.sections.map((section, index) => (
                <section key={section.heading} id={`section-${index}`} className="mt-10 scroll-mt-28">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    {section.heading}
                  </h2>
                  <div className="mt-4 space-y-4">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph.slice(0, 24)} className="text-sm leading-8 text-foreground-700">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </section>
              ))}

              <section className="mt-10 rounded-lg border border-secondary-200 bg-secondary-50 p-5 md:p-6">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-secondary-900">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-shield-check-line text-base"></i>
                  </span>
                  你可以马上做的几件事
                </h2>
                <ol className="mt-4 space-y-3">
                  {incident.advice.map((item, index) => (
                    <li key={item} className="flex items-start gap-3 text-sm leading-6 text-foreground-700">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary-600 text-[11px] font-medium text-background-50">
                        {index + 1}
                      </span>
                      {item}
                    </li>
                  ))}
                </ol>
              </section>

              <section className="mt-10">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">事件时间线</h2>
                <ol className="mt-5 border-l border-background-300 pl-6">
                  {incident.timeline.map((step) => (
                    <li key={step.date} className="relative pb-6 last:pb-0">
                      <span className="absolute -left-[30px] top-1 flex h-3 w-3 items-center justify-center rounded-full border-2 border-primary-500 bg-background-50"></span>
                      <p className="text-xs font-medium text-primary-700">{step.date}</p>
                      <p className="mt-1 text-sm leading-6 text-foreground-700">{step.text}</p>
                    </li>
                  ))}
                </ol>
              </section>
            </div>

            <aside className="min-w-0">
              <div className="sticky top-28 space-y-6">
                <nav className="rounded-lg border border-background-200 bg-background-50 p-5" aria-label="内容目录">
                  <h2 className="font-heading text-sm font-bold text-foreground-950">本文目录</h2>
                  <ul className="mt-3 space-y-2 text-sm">
                    <li>
                      <a href="#section-0" className="text-foreground-600 transition-colors hover:text-primary-700">
                        事件概述与成因
                      </a>
                    </li>
                    <li>
                      <a href="#section-1" className="text-foreground-600 transition-colors hover:text-primary-700">
                        风险解读
                      </a>
                    </li>
                    <li>
                      <a href="#related-incidents" className="text-foreground-600 transition-colors hover:text-primary-700">
                        相关事件推荐
                      </a>
                    </li>
                  </ul>
                </nav>

                <div className="rounded-lg border border-background-200 bg-foreground-950 p-5">
                  <h2 className="font-heading text-sm font-bold text-background-50">遇到过类似情况？</h2>
                  <p className="mt-2 text-xs leading-6 text-background-200">
                    你的经历可能正好能提醒其他人。报料可匿名，我们会核实后再决定是否公开。
                  </p>
                  <Link
                    to="/contact"
                    className="mt-4 block whitespace-nowrap rounded-md bg-primary-600 px-4 py-2.5 text-center text-sm text-background-50 transition-colors hover:bg-primary-500"
                  >
                    提交线索
                  </Link>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-100/70 p-5">
                  <h2 className="font-heading text-sm font-bold text-foreground-950">同类事件</h2>
                  <ul className="mt-3 space-y-3">
                    {relatedList.map((item) => (
                      <li key={item.id}>
                        <Link
                          to={`/incident/${item.id}`}
                          className="text-sm leading-6 text-foreground-600 transition-colors hover:text-primary-700"
                        >
                          {item.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/incidents"
                    className="mt-4 inline-flex items-center gap-1 text-sm text-primary-700"
                  >
                    全部事件
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-arrow-right-line text-sm"></i>
                    </span>
                  </Link>
                </div>
              </div>
            </aside>
          </div>

          <section id="related-incidents" className="mt-14 scroll-mt-28">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关事件推荐</h2>
            <div className="mt-5 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {incidents
                .filter((item) => item.id !== incident.id)
                .slice(0, 3)
                .map((item) => (
                  <IncidentCard key={item.id} incident={item} />
                ))}
            </div>
          </section>
        </div>
      </article>
    </>
  );
}